import type { Article } from '@/mocks/articles';

const SITE_URL: string = (import.meta.env.VITE_SITE_URL || '').replace(/\/$/, '');

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'BasetaoSheet',
  url: SITE_URL,
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Basetao Spreadsheet Resource Hub',
  url: SITE_URL,
  description:
    'Complete Basetao Spreadsheet shopping guide with product categories, reviews, shipping costs, coupons, and warehouse tips.',
  publisher: {
    '@type': 'Organization',
    name: 'BasetaoSheet',
  },
};

export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Basetao Spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Basetao Spreadsheet is an organized document that compiles product links, pricing details, and shopping insights for products available through the Basetao platform. It helps shoppers discover products, compare prices, and make informed buying decisions with curated information organized by category.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Basetao legit and safe to use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Basetao is a legitimate shopping agent platform that has served thousands of international customers. The platform connects buyers with products from Chinese marketplaces, handles quality inspection, and manages international shipping. Always verify seller ratings and use the platform\u2019s buyer protection features for a safe shopping experience.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does Basetao shipping cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Basetao shipping costs vary based on package weight, dimensions, destination country, and shipping method selected. The platform offers multiple shipping options including economy air mail, express carriers, and sea freight. Using a Basetao Spreadsheet can help you estimate and compare shipping costs across different products and methods.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Basetao offer coupons or discount codes?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Basetao periodically offers promotional coupons and discount codes for both new and returning customers. These can include shipping discounts, service fee reductions, and seasonal promotions. Check the Basetao Spreadsheet resources regularly for updated coupon information and money-saving tips.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where is the Basetao warehouse located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Basetao operates warehouse facilities in China where purchased products are received, inspected, and stored before international shipping. The warehouse team performs quality checks, takes inspection photos, and consolidates multiple orders into single shipments to help reduce overall shipping costs.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I use Basetao Spreadsheet to shop better?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'To use a Basetao Spreadsheet effectively, browse the organized product categories to discover trending finds, compare prices and quality notes, refer to shipping cost estimates to plan your budget, check for available coupon codes, and use the community insights to avoid common purchasing mistakes.',
      },
    },
  ],
};

export function articleSchema(article: Article) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.imageUrl,
    datePublished: '2026-08-11',
    dateModified: '2026-08-11',
    author: {
      '@type': 'Organization',
      name: 'BasetaoSheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'BasetaoSheet',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${article.path}`,
    },
  };
}

export function breadcrumbSchema(article: Article) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `${SITE_URL}${article.path}`,
      },
    ],
  };
}